import { useEffect, useState } from "react";
import { Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { LogementsPanel, RappelsPanel } from "@/components/SettingsPanels";
import { ReglesPanel } from "@/components/ReglesPanel";
import { EffacerPanel } from "@/components/EffacerPanel";
import { MotDePassePanel } from "@/components/MotDePassePanel";
import { ComptablePanel } from "@/components/ComptablePanel";
import { ConnexionsPanel } from "@/components/ConnexionsPanel";
import { AppareilsPanel } from "@/components/AppareilsPanel";
import { TauxTalPanel } from "@/components/TauxTalPanel";
import { BookingTaxePanel } from "@/components/BookingTaxePanel";
import { SauvegardePanel } from "@/components/SauvegardePanel";
import { FacturesImmeublePanel } from "@/components/FacturesImmeublePanel";
import { CategoryRow } from "@/components/CategorieRow";
import { TypesRevenusAuto } from "@/components/TypesRevenusAuto";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export const HOTE_FIELDS = [
  ["nom", "Nom de l'hôte"], ["adresse", "Adresse"], ["no_tps", "N° TPS"], ["no_tvq", "N° TVQ"], ["no_hebergement", "N° d'établissement (CITQ)"],
];

const versListe = (d) => Object.entries(d || {}).map(([nom, mots]) => ({ nom, mots: mots.join(", "), existant: true }));

export default function Parametres() {
  const { etat } = useAuth();
  const [info, setInfo] = useState(null);
  const [cats, setCats] = useState([]);
  const [revs, setRevs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [proteges, setProteges] = useState({ depense: [], revenu: [] });

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setInfo(data.info_hote);
      setCats(versListe(data.categories));
      setRevs(versListe(data.categories_revenus));
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/categories/protegees").then((r) => setProteges(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    const versDict = (l) => Object.fromEntries(l.filter((c) => c.nom.trim()).map((c) => [c.nom.trim(), c.mots.split(",").map((m) => m.trim()).filter(Boolean)]));
    try {
      const { data } = await api.put("/settings", { info_hote: info, categories: versDict(cats), categories_revenus: versDict(revs) });
      setCats(versListe(data.categories));
      setRevs(versListe(data.categories_revenus));
      toast.success("Paramètres enregistrés");
    }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!info) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;

  return (
    <div data-testid="parametres-page">
      <PageHeader eyebrow="Configuration" title="Paramètres">
        <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-settings-button">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Enregistrer
        </Button>
      </PageHeader>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <Panel title="Informations de l'hôte" className="xl:col-span-2" testId="host-info-panel">
          <p className="text-xs text-slate-500 mb-4">Utilisées dans les rapports d'erreur et les lettres de contestation.</p>
          <div className="space-y-3">
            {HOTE_FIELDS.map(([k, l]) => (
              <div key={k} className="space-y-1">
                <Label>{l}</Label>
                <Input value={info[k] || ""} onChange={(e) => setInfo({ ...info, [k]: e.target.value })} data-testid={`host-${k}-input`} />
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Catégories de dépenses" className="xl:col-span-3" testId="categories-panel"
          action={<Button size="sm" variant="outline" onClick={() => setCats([...cats, { nom: "", mots: "" }])} data-testid="add-category-button"><Plus className="h-3.5 w-3.5 mr-1" />Catégorie</Button>}>
          <p className="text-xs text-slate-500 mb-4">Une opération bancaire est classée dans la première catégorie dont un mot-clé apparaît dans sa description. Le crayon renomme une catégorie partout, y compris dans les opérations déjà classées et les mémorisations.</p>
          <div className="space-y-2">
            {cats.map((c, i) => (
              <CategoryRow key={i} cat={c} idx={i} protegee={proteges.depense.includes(c.nom)} onChange={(idx, v) => setCats(cats.map((x, j) => (j === idx ? v : x)))} onRemove={(idx) => setCats(cats.filter((_, j) => j !== idx))} />
            ))}
          </div>
        </Panel>
      </div>
      <Panel title="Types de revenus" className="mt-6" testId="revenus-types-panel"
        action={<Button size="sm" variant="outline" onClick={() => setRevs([...revs, { nom: "", mots: "" }])} data-testid="add-revenu-type-button"><Plus className="h-3.5 w-3.5 mr-1" />Type de revenu</Button>}>
        <p className="text-xs text-slate-500 mb-4">Classement automatique des dépôts : d'abord les dépôts qui règlent une réclamation Airbnb/Booking (Régularisation), puis le premier type ci-dessous dont un mot-clé apparaît dans la description (le crayon renomme un type partout, y compris dans les opérations déjà classées). Les autres dépôts rattachés à un immeuble (Airbnb, Booking, loyers, commercial) vont dans « Revenu &lt;type de l'immeuble&gt; &lt;immeuble&gt; » — le type se règle dans Immeubles. Le reste va dans « Autres revenus ».</p>
        <div className="space-y-2">
          {revs.map((c, i) => (
            <CategoryRow key={i} cat={c} idx={i} prefix="revenu-type" genre="revenu" protegee={proteges.revenu.includes(c.nom)} onChange={(idx, v) => setRevs(revs.map((x, j) => (j === idx ? v : x)))} onRemove={(idx) => setRevs(revs.filter((_, j) => j !== idx))} />
          ))}
        </div>
        <TypesRevenusAuto />
      </Panel>
      <div className="mt-6"><ReglesPanel /></div>
      <div className="mt-6"><BookingTaxePanel /></div>
      <div className="mt-6"><EffacerPanel /></div>
      <div className="mt-6"><RappelsPanel /></div>
      <div className="mt-6"><LogementsPanel /></div>
      <div className="mt-6"><FacturesImmeublePanel /></div>
      <div className="mt-6"><ComptablePanel /></div>
      {etat?.local && <div className="mt-6"><SauvegardePanel /></div>}
      <div className="mt-6"><MotDePassePanel /></div>
      {!etat?.local && <div className="mt-6"><AppareilsPanel /></div>}
      <div className="mt-6"><TauxTalPanel /></div>
      <div className="mt-6 scroll-mt-6" id="connexions"><ConnexionsPanel /></div>
    </div>
  );
}
